import { PayloadAction } from '@reduxjs/toolkit';
import { isDefined } from '@yandex-int/messenger.utils';
import {
    ActiveDevices,
    DeviceOrQuery,
    DevicesChangedEvent,
    Device as GoloomDevice,
    QueryDevices,
    SelectedDevices,
} from '@yandex-video-platform/goloom-sdk';
import { call, put, select } from 'typed-redux-saga';

import { setCookieByConfig } from '../../../../../shared/features/config/sagas';
import { selectIsOnPremise } from '../../../../../shared/features/config/selectors';
import { deviceManager } from '../../../../../shared/features/goloom-sdk';
import {
    FacingMode,
    getAudioInputDeviceSelector,
    getAudioOutputDeviceSelector,
    getIsOutputDeviceChangeAvailable,
    getVideoFacingMode,
    getVideoInputDeviceSelector,
    setAudioInputDeviceAction,
    setAudioOutputDeviceAction,
    SetDeviceActionPayload,
    setStoreAudioInputDeviceIdAction,
    setStoreAudioOutputDeviceIdAction,
    setStoreVideoInputDeviceIdAction,
    setVideoFacingModeAction,
    setVideoInputDeviceAction,
    toggleCameraFacingModeAction,
} from '../../../../../shared/features/settings';
import { refreshMediaDevices } from '../../../../../shared/features/settings/sagas';
import { isMobileUa } from '../../../../../shared/features/uatraits/selectors';
import { IGNORE_DEVICE_IDS } from '../../../../../shared/helpers/constants';
import CookieManager from '../../../../../shared/helpers/cookie-manager';
import { ACTION, FEATURE } from '../../../../../shared/helpers/logger-enums';
import { createLogger } from '../../../../../shared/helpers/loggerFacade';
import { createQueueSaga, takeLatestThrottled } from '../../../../../shared/helpers/redux-saga';
import Storage from '../../../../../shared/helpers/storage';
import {
    AudioDeviceConstraints,
    getDesktopVideoConstraints,
    getMobileVideoConstraints,
    getVideoConstraints,
    VideoDeviceConstraints,
} from '../../../../../shared/helpers/video-resolution-constraints';
import { getSelfMutedState } from '../../../selectors';

interface RequestedDevices {
    audio?: string | boolean;
    video?: string | boolean;
    audioOutput?: string | boolean;
}

interface RequestedDevicesConstraints {
    video?: VideoDeviceConstraints;
}

const logger = createLogger('goloom:device');

function findDeviceById(deviceId: Maybe<string>, goloomDevices: Array<GoloomDevice>): Maybe<GoloomDevice> {
    if (deviceId === undefined || !goloomDevices.length) {
        return;
    }

    return (
        goloomDevices.find((device) => device.id === deviceId) ||
        goloomDevices.find((device) => device.id === 'default') ||
        goloomDevices[0]
    );
}

function* getDeviceOrQuery(
    devices: Array<GoloomDevice>,
    deviceId: Maybe<string | boolean>,
    kind: MediaDeviceKind,
    constraints?: AudioDeviceConstraints | VideoDeviceConstraints
) {
    const device: Maybe<DeviceOrQuery> = yield* call(function* () {
        if (typeof deviceId === 'string') {
            const device = yield* call(findDeviceById, deviceId, devices);
            return device || ({ kind } as DeviceOrQuery);
        } else if (deviceId) {
            return { kind } as DeviceOrQuery;
        }
    });
    if (device && constraints) {
        device.constraints = constraints;
    }
    return device;
}
function* getGoloomDevicesSaga(requestedDevices: RequestedDevices, constraints?: RequestedDevicesConstraints) {
    const query: QueryDevices = {
        audio: Boolean(requestedDevices.audio || requestedDevices.audioOutput),
        video: Boolean(requestedDevices.video),
    };

    const devices = yield* call([deviceManager, deviceManager.requestDevices], query);
    const audioDevices: GoloomDevice[] = devices.audioInput.filter(({ id }) => !IGNORE_DEVICE_IDS.has(id));
    const videoDevices: GoloomDevice[] = devices.videoInput.filter(({ id }) => !IGNORE_DEVICE_IDS.has(id));
    const audioOutputDevices: GoloomDevice[] = devices.audioOutput.filter(({ id }) => !IGNORE_DEVICE_IDS.has(id));

    const audioInputDevice = yield* call(getDeviceOrQuery, audioDevices, requestedDevices.audio, 'audioinput');
    const audioOutputDevice = yield* call(
        getDeviceOrQuery,
        audioOutputDevices,
        requestedDevices.audioOutput,
        'audiooutput'
    );
    const videoInputDevice = yield* call(
        getDeviceOrQuery,
        videoDevices,
        requestedDevices.video,
        'videoinput',
        constraints?.video
    );

    const isOutputDeviceChangeAvailable = yield* select(getIsOutputDeviceChangeAvailable);

    return {
        audio: audioInputDevice,
        video: videoInputDevice,
        audioOutput: isOutputDeviceChangeAvailable ? audioOutputDevice : undefined,
    };
}

/**
 * Установка девайсов в sdk
 *
 * @param devices - string для конкретных девайсов, bool для запроса по ограничениям.
 * @param constraints - ограничения на запрос
 * @param isInitCall - вызов из initDevices
 * @return true, если нет ошибок
 */
function* setDevicesSaga({
    devices,
    constraints,
    isInitCall,
}: {
    devices: RequestedDevices;
    constraints?: RequestedDevicesConstraints;
    isInitCall?: boolean;
}) {
    const desiredDevices = yield* call(getGoloomDevicesSaga, devices, constraints);
    const mutedState = yield* select(getSelfMutedState);
    const isMobile = yield* select(isMobileUa);
    const { videoMuted = false } = mutedState ?? {};

    try {
        /** Предварительно отпускаем камеру, чтоб не поймать DEVICE_BUSY на некоторых устройствах */
        const isVideoActiveAndRequested = !videoMuted && desiredDevices.video;
        if (isMobile && !isInitCall && isVideoActiveAndRequested) {
            yield* call([deviceManager, deviceManager.setVideoStatus], false);
        }
        yield* call([deviceManager, deviceManager.setDevices], Object.values(desiredDevices).filter(isDefined));
        yield* call([deviceManager, deviceManager.syncMuteState], 'all');
        if (isMobile && !isInitCall && isVideoActiveAndRequested) {
            yield* call([deviceManager, deviceManager.setVideoStatus], true);
        }
        return true;
    } catch (error) {
        logger.error(error, {
            method: 'setDevicesSaga',
            additional: {
                devices,
                constraints,
            },
        });
    }
}

const [setDevicesQueueWatcher, setDevicesQueuedSaga] = createQueueSaga(setDevicesSaga);

/**
 * Сага смены аудио девайса
 */
function* setAudioInputDeviceWorker({ payload: { deviceId } }: PayloadAction<SetDeviceActionPayload>) {
    const success = yield* call(setDevicesQueuedSaga, { devices: { audio: deviceId } });
    logger.endFeatureDelta({
        feature: FEATURE.DEVICES,
        action: ACTION.SELECT_MIC,
        isSuccess: Boolean(success),
        options: {
            additional: {
                deviceId,
            },
        },
    });
}

/**
 * Сага смены аудио девайса
 */
function* setAudioOutputDeviceWorker({ payload: { deviceId } }: PayloadAction<SetDeviceActionPayload>) {
    const success = yield* call(setDevicesQueuedSaga, { devices: { audioOutput: deviceId } });
    logger.endFeatureDelta({
        feature: FEATURE.DEVICES,
        action: ACTION.SELECT_SPEAKER,
        isSuccess: Boolean(success),
        options: {
            additional: {
                deviceId,
            },
        },
    });
}

/**
 * Сага смены видео девайса
 */
function* setVideoInputDeviceWorker({ payload: { deviceId } }: PayloadAction<SetDeviceActionPayload>) {
    const videoConstraints = getDesktopVideoConstraints();
    const success = yield* call(setDevicesQueuedSaga, {
        devices: { video: deviceId },
        constraints: { video: videoConstraints },
    });
    logger.endFeatureDelta({
        feature: FEATURE.DEVICES,
        action: ACTION.SELECT_CAMERA,
        isSuccess: Boolean(success),
        options: {
            additional: {
                deviceId,
            },
        },
    });
}

function* setVideoInputDeviceByFacingWorker({ payload: facingMode }: PayloadAction<FacingMode>) {
    const constraints = yield* call(getMobileVideoConstraints, facingMode);
    const isDevicesSet = yield* call(setDevicesQueuedSaga, {
        devices: { video: true },
        constraints: { video: constraints },
    });
    if (isDevicesSet) {
        yield* put(setVideoFacingModeAction(facingMode));
        yield* call([Storage, Storage.setItem], 'videoInputFacingMode', facingMode);

        const isOnPremise = yield* select(selectIsOnPremise);
        if (!isOnPremise) {
            yield* call([CookieManager, setCookieByConfig], 'telemostVideoInputFacingMode', facingMode);
        }
    }
}

/**
 * Слежение за событиями смены девайсов во время конференции
 */
export function* watchDeviceChange() {
    yield* takeLatestThrottled(3000, setAudioInputDeviceAction, setAudioInputDeviceWorker);
    yield* takeLatestThrottled(3000, setAudioOutputDeviceAction, setAudioOutputDeviceWorker);
    yield* takeLatestThrottled(3000, setVideoInputDeviceAction, setVideoInputDeviceWorker);
    yield* takeLatestThrottled(3000, toggleCameraFacingModeAction, setVideoInputDeviceByFacingWorker);
}

export function* initDevices(params?: { audio?: boolean; video?: boolean }) {
    logger.event('Devices initialization', {
        additional: {
            method: 'initDevices',
            params,
        },
    });

    const { audio = true, video = true } = params ?? {};
    const isMobile = yield* select(isMobileUa);
    yield* call(refreshMediaDevices);

    const facingMode = yield* select(getVideoFacingMode);
    const videoConstraints = yield* call(getVideoConstraints, isMobile, facingMode);
    const constraints = { video: videoConstraints };
    if (isMobile) {
        yield call(setDevicesQueuedSaga, {
            devices: {
                audio: audio,
                video: video,
                audioOutput: audio,
            },
            constraints,
            isInitCall: true,
        });
    } else {
        const videoDevice = yield* select(getVideoInputDeviceSelector);
        const audioDevice = yield* select(getAudioInputDeviceSelector);
        const audioOutputDevice = yield* select(getAudioOutputDeviceSelector);
        yield call(setDevicesQueuedSaga, {
            devices: {
                audio: audio && audioDevice ? audioDevice.deviceId : undefined,
                video: video && videoDevice ? videoDevice.deviceId : undefined,
                audioOutput: audio && audioOutputDevice ? audioOutputDevice.deviceId : undefined,
            },
            isInitCall: true,
            constraints,
        });
    }

    // @ts-expect-error -- Safari-only property
    if (navigator.audioSession) {
        // @ts-expect-error -- Safari-only property
        navigator.audioSession.type = 'play-and-record';
    }
}

const getMissingDeviceKind = (devices: SelectedDevices): Set<'audioinput' | 'audiooutput' | 'videoinput'> => {
    const missingDevices = new Set<'audioinput' | 'audiooutput' | 'videoinput'>();
    if (!devices.audioInput) {
        missingDevices.add('audioinput');
    }
    if (!devices.audioOutput) {
        missingDevices.add('audiooutput');
    }
    if (!devices.videoInput) {
        missingDevices.add('videoinput');
    }
    return missingDevices;
};

export function* changeDeviceSaga(event: DevicesChangedEvent) {
    const selectedDevices = deviceManager.selectedDevices;
    const selectedDevicesArray = [
        selectedDevices?.audioInput,
        selectedDevices?.videoInput,
        selectedDevices?.audioOutput,
    ].filter(Boolean) as DeviceOrQuery[];

    const removedSet = new Set(event.removed.map((device) => device.id));
    const missingDevices = getMissingDeviceKind(selectedDevices || ({} as SelectedDevices));

    const addedMissingDevices = event.added.some((device) => missingDevices.has(device.kind));
    const removedSelectedDevice =
        removedSet.size > 0 &&
        selectedDevicesArray.some((device) => {
            // Проверка на id нужна чтобы определять, мы выбрали конкретный device (есть id), или deviceQuery (нет id).
            // И если какой-то из выбранных девайсов был удален, то нужно переинициализировать девайсы.
            // Если у нас выбран deviceQuery, то делаем reinit в любом случае.
            if ('id' in device) {
                return removedSet.has(device.id);
            }
            return true;
        });
    const needApply = removedSelectedDevice || addedMissingDevices;
    if (needApply) {
        yield* call(initDevices);
    }
}

export function* onActiveDeviceChangedSaga(activeDevices: ActiveDevices) {
    if (activeDevices.audioInput) {
        yield* put(setStoreAudioInputDeviceIdAction(activeDevices.audioInput.id));
    }
    if (activeDevices.audioOutput) {
        yield* put(setStoreAudioOutputDeviceIdAction(activeDevices.audioOutput.id));
    }
    if (activeDevices.videoInput) {
        yield* put(setStoreVideoInputDeviceIdAction(activeDevices.videoInput.id));
    }
}
export { setDevicesQueueWatcher };
